import { Button, ButtonProps } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  AnimatePresence,
  motion,
  MotionValue,
  useScroll,
  useTransform,
} from "framer-motion";

import { ArrowRight } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import ImageSvg from "../ui/image-svg";

type ImageProps = {
  src: string;
  alt?: string;
};

type ContentProps = {
  heading: string;
  description: string;
  color: string;
  image: ImageProps;
  imageMobile: ImageProps;
  dots: { text: string }[];
  descriptionFinal: string;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  data: ContentProps[];
};

export type WhereSectionProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

type WhereAnimatedCardProps = {
  item: ContentProps;
  index: number;
  total: number;
  isMobile: boolean;
  scrollYProgress: MotionValue<number>;
};

function WhereAnimatedCard({
  item,
  index,
  total,
  isMobile,
  scrollYProgress,
}: WhereAnimatedCardProps) {
  const startProgress = index / total;
  const endProgress = (index + 1) / total;
  const opacity = useTransform(
    scrollYProgress,
    [
      Math.max(0, startProgress - 0.07),
      startProgress,
      endProgress - 0.07,
      Math.min(1, endProgress),
    ],
    [0, 1, 1, 0],
  );
  const y = useTransform(
    scrollYProgress,
    [
      Math.max(0, startProgress - 0.1),
      startProgress,
      endProgress - 0.1,
      Math.min(1, endProgress),
    ],
    [100, 0, 0, -100],
  );

  const motionStyles = {
    opacity,
    y,
  };

  if (isMobile) {
    return (
      <div>
        <div className={`p-5 my-4 rounded-2xl ${item.color} text-n-text-green`}>
          <h3 className="mb-3 text-2xl font-semibold ">{item.heading}</h3>
          <p className="text-sm">{item.description}</p>
          <ul className="mt-4 list-inside list-disc space-y-2">
            {item.dots.map((dot, dotIndex) => (
              <li key={dotIndex} className="text-sm">
                {dot.text}
              </li>
            ))}
          </ul>
          {item.descriptionFinal && (
            <p className="mt-4 text-sm ">{item.descriptionFinal}</p>
          )}
        </div>
        <div className="mt-10">
          <ImageSvg
            src={item.imageMobile.src}
            alt={item.imageMobile.alt}
            className="w-[60%] mx-auto"
          />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      style={motionStyles}
      initial={index === 0 ? { opacity: 0, y: 100 } : false}
      animate={index === 0 ? { opacity: 1, y: 0 } : {}}
      transition={index === 0 ? { duration: 0.5 } : {}}
      className={`md:absolute first:md:relative p-5 rounded-xl ${item.color}`}
    >
      <h3 className="font-semibold md:mb-4 md:text-3xl text-n-text-green">
        {item.heading}
      </h3>
      <p className="text-n-text-green text-lg">{item.description}</p>
      <ul className="mt-4 list-inside list-disc space-y-2">
        {item.dots.map((dot, dotIndex) => (
          <li key={dotIndex} className="text-lg text-n-text-green">
            {dot.text}
          </li>
        ))}
      </ul>
      {item.descriptionFinal && (
        <p className="mt-4 text-lg text-n-text-green">
          {item.descriptionFinal}
        </p>
      )}
    </motion.div>
  );
}

export const WhereSection = (props: WhereSectionProps) => {
  const { data, tagline, heading, description, buttons } = {
    ...WhereSectionDefaults,
    ...props,
  };
  const isMobile = useMediaQuery("(max-width: 767px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  return (
    <section
      ref={containerRef}
      id="para-quem"
      className=" py-10 md:py-24 bg-n-bg-green"
    >
      <div className="container">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start md:gap-20">
          <div className="flex flex-col gap-y-16 md:sticky md:top-20 md:mt-20 md:h-[calc(100vh_-10rem)] md:justify-center">
            <div className=" flex 2xl:flex flex-col">
              <p className="mb-3 font-semibold md:mb-4">{tagline}</p>
              <h2
                className="mb-5 text-3xl md:mb-6 lg:text-5xl text-n-text-beige"
                style={{
                  fontFamily: "'Anton', sans-serif",
                  lineHeight: "1.1em",
                }}
              >
                {heading}
              </h2>
              <p className="xl:hidden flex md:text-md text-n-text-light font-light">
                {description}
              </p>
              <div className="mt-6 xl:hidden flex flex-wrap items-center gap-4 md:mt-8">
                {buttons.map((button, buttonIndex) => (
                  <Link to="/fazer-cotacao" key={buttonIndex}>
                    <Button {...button}>
                      {button.title}
                      <ArrowRight className="ml-2 " />
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-start gap-y-8">
              <AnimatePresence>
                {data.map((item, index) => {
                  return (
                    <WhereAnimatedCard
                      key={index}
                      item={item}
                      index={index}
                      total={data.length}
                      isMobile={isMobile}
                      scrollYProgress={scrollYProgress}
                    />
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
          <div className="hidden md:grid md:grid-cols-1 md:gap-4">
            {data.map((item, index) => (
              <div key={index} className="h-screen overflow-hidden md:mt-40">
                <ImageSvg
                  src={item.image.src}
                  alt={item.image.alt}
                  className="w-[90%] h-auto mx-auto"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const WhereSectionDefaults: Props = {
  tagline: "",
  heading:
    "Especialistas em transporte de carros com soluções pensadas para você e seu negócio.",
  description:
    "Transporte individual com proteção completa, agilidade e atendimento sob medida",
  buttons: [
    { title: "Fazer cotação", variant: "default", id: "btn-fazer-cotacao" },
  ],
  data: [
    {
      heading: "Plataformas e e-commerces",
      description:
        "Se você atua na venda digital de veículos, sabe que o transporte é um fator decisivo para a satisfação do cliente final. A Nacional GO integra-se ao modelo de negócios de e-commerces e plataformas digitais, oferecendo:",
      dots: [
        { text: "Logística nacional para entregas em todo o Brasil." },
        {
          text: "Integração tecnológica para gestão de pedidos e acompanhamento em tempo real.",
        },
      ],
      descriptionFinal:
        "Experiência do consumidor final garantida com prazos cumpridos e comunicação transparente.",
      color: "bg-n-green-s-light",
      image: {
        src: "phone-car.svg",
        alt: "Plataformas e e-commerces",
      },
      imageMobile: {
        src: "phone-car.svg",
        alt: "Plataformas e e-commerces",
      },
    },
    {
      heading: "Frotistas e locadoras",
      description:
        "Gestores de frota e locadoras de veículos demandam agilidade, rastreabilidade e custo otimizado. A Nacional GO entrega:",
      dots: [
        { text: "Transporte de veículos em volume, com rotas inteligentes." },
        { text: "Redução de custos operacionais com soluções sob medida." },
        {
          text: "Controle e rastreamento centralizados para maior previsibilidade.",
        },
        { text: "Apoio em realocação rápida de veículos entre unidades." },
      ],
      descriptionFinal: "",
      color: "bg-n-beige",
      image: {
        src: "frota.svg",
        alt: "Gestores de frota e locadoras de veículos",
      },
      imageMobile: {
        src: "frota.svg",
        alt: "Gestores de frota e locadoras de veículos",
      },
    },
    {
      heading: "Leilões e marketplaces",
      description:
        "O setor de leilões e marketplaces exige logística confiável e ágil para retirada e entrega de veículos. A Nacional GO é referência nesse segmento e oferece:",
      dots: [
        { text: "Retirada em pátios de leilão com equipe especializada." },
        {
          text: "Segurança completa para veículos sinistrados ou de alto valor.",
        },
        {
          text: "Cobertura nacional para entregas a compradores em qualquer região.",
        },
      ],
      descriptionFinal: "Suporte na documentação e prazos de liberação.",
      color: "bg-n-bg-1",
      image: {
        src: "leilao.svg",
        alt: "Leilões e marketplaces",
      },
      imageMobile: {
        src: "leilao.svg",
        alt: "Leilões e marketplaces",
      },
    },
    {
      heading: "Relocação corporativa",
      description:
        "Empresas que transferem colaboradores para novas cidades ou estados precisam de parceiros que garantam segurança e pontualidade. A Nacional GO oferece:",
      dots: [
        {
          text: "Transporte porta a porta para maior comodidade do colaborador.",
        },
        {
          text: "Atendimento personalizado e planejamento logístico conforme a política de mobilidade da empresa.",
        },
        {
          text: "Agilidade na programação para acompanhar o cronograma corporativo.",
        },
      ],
      descriptionFinal:
        "Soluções sob medida para executivos, equipes e servidores em transferência.",
      color: "bg-n-green-s-light",
      image: {
        src: "relocacao-corporativa.svg",
        alt: "Relocação corporativa",
      },
      imageMobile: {
        src: "relocacao-corporativa.svg",
        alt: "Relocação corporativa",
      },
    },
  ],
};
